import type { FinanstilsynetFilters } from '../../../../../../types/internal-types'

export type FinanstilsynetDateRange = [Date | null, Date | null]

/**
 * Default Finanstilsynet reporting window. Matches the backend fallback in
 * <c>FinanstilsynetFilterDto.DefaultFrom / DefaultTo</c>.
 *
 * These are intentionally constructed as *local-midnight* Dates so that
 * `DatePickerInput` — which formats values in the viewer's local timezone —
 * shows "1 Jan 2025" and "28 Feb 2026" regardless of UTC offset. The UTC
 * end-of-day expansion needed for an inclusive `To` filter happens later in
 * `rangeToFilters`, not here.
 */
export const DEFAULT_FROM = new Date(2025, 0, 1)
export const DEFAULT_TO = new Date(2026, 1, 28)

export const DEFAULT_RANGE: FinanstilsynetDateRange = [DEFAULT_FROM, DEFAULT_TO]

/**
 * Serializes a date-picker range into the filter payload expected by the
 * backend. The `to` bound is snapped to end-of-day (UTC) so the selected day
 * is inclusive — without this, picking "2026-02-28" would mean
 * "strictly before midnight on the 28th".
 */
export const rangeToFilters = (
  range: FinanstilsynetDateRange
): FinanstilsynetFilters => {
  const [from, to] = range
  return {
    from: from
      ? new Date(
          Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0)
        ).toISOString()
      : undefined,
    to: to
      ? new Date(
          Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999)
        ).toISOString()
      : undefined,
  }
}

/**
 * Builds the `?From=…&To=…` suffix for CSV download URLs so the exported file
 * respects the same date range as the on-screen preview.
 */
export const rangeToCsvQuery = (range: FinanstilsynetDateRange): string => {
  const { from, to } = rangeToFilters(range)
  const sp = new URLSearchParams()
  if (from) sp.append('From', from)
  if (to) sp.append('To', to)
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}
